import { createFileRoute } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Slice } from '@furality/furality-purpose';
import LinkButton from '@/components/LinkButton.tsx';
import { RegisterLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PartyHostAddOnContainer from '@/components/pass/PartyHostAddOnContainer.tsx';
import PerksSection from '@/components/pass/PerksSection.tsx';
import PricingSection from '@/components/pass/PricingSection.tsx';
import { getPassDetails } from '@/components/pass/registration.ts';
import useFlag from '@/lib/hooks/useFlag.ts';
import useRegistrationQuery from '@/lib/queries/useRegistrationQuery.ts';
import useCart from '@/lib/useCart.tsx';

const Upgrade = () => {
  const { t } = useTranslation(['common', 'register', 'upgrade']);

  const regStatus = useRegistrationQuery();
  const { cart, upgradeInCart, products } = useCart();
  const partyHostInCart = cart.data?.items.find((i) => i.type === 'addon');
  const hasUpgradeAvailable = products.data?.some((product) => product.type === 'upgrade');
  const hasAddonAvailable = products.data?.some((product) => product.type === 'addon');
  const hasAnyUpgradeAvailable = hasUpgradeAvailable || hasAddonAvailable;
  const hasItemsInCart = cart.data?.items && cart.data.items.length > 0;
  const checkoutButtonDisabled = !(upgradeInCart || partyHostInCart);

  const currentLevel = regStatus.data?.registrationLevel ?? 'attendee';
  const currentLevelDetails = getPassDetails(currentLevel);
  const checkoutRef = useRef<HTMLDivElement>(null);

  const form = useForm<{ selectedCurrency: string }>({
    defaultValues: {
      selectedCurrency: 'USD',
    },
  });

  const navigation = {
    nextStep: () => checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
    prevStep: () => {},
    step: 1,
    setStep: () => {},
  };

  const featuresReady = useFlagsStatus().flagsReady;
  const regIsOpen = useFlag('registration');

  if (!featuresReady || regStatus.isLoading) {
    return <PageLoading />;
  }

  if (!regIsOpen) {
    return (
      <Slice topDelimiter bottomDelimiter size="lg">
        {t('shared.registration_closed')}
      </Slice>
    );
  }

  return (
    <FormProvider {...form}>
      <Slice topDelimiter bottomDelimiter size="lg">
        <div className="mb-5 text-center">
          {/** biome-ignore lint/correctness/useUniqueElementIds: intentionally static */}
          <h1 id="active-step" className="mt-4">
            {t('upgrade:title')}
          </h1>
          <h5>{t('upgrade:highlight', { pass: t(currentLevelDetails.label) })}</h5>
          <h2 className="lead lh-180 mb-6">
            {t(hasAnyUpgradeAvailable ? 'upgrade:available' : 'upgrade:not_available')}
          </h2>
          {hasUpgradeAvailable && (
            <>
              <PricingSection className="mb-6" navigation={navigation} />
              <PerksSection className="mb-6 text-left" />
            </>
          )}
          {hasAddonAvailable && <PartyHostAddOnContainer className="mb-5 text-left" />}
          {hasItemsInCart && (
            <div className="mb-6">
              <p className="lead">{t('upgrade:items_in_cart')}</p>
            </div>
          )}
          <LinkButton to="/profile" variant="secondary" className="mb-4">
            {t('shared.return_to_profile')}
          </LinkButton>
          {hasItemsInCart && (
            <LinkButton to="/checkout" className="mb-4" disabled={checkoutButtonDisabled}>
              {t('shared.continue')}
            </LinkButton>
          )}
          <div ref={checkoutRef} />
        </div>
      </Slice>
    </FormProvider>
  );
};

export const Route = createFileRoute('/upgrade')({
  component: () => (
    <RegisterLayout title="shared.navigation.upgrade">
      <Upgrade />
    </RegisterLayout>
  ),
});
